import axios from 'axios'
import { handleApiError } from '@/utils/apiHelpers'

export default {
    getComplaints({ commit }, payload = {
        paginate: '',
        query: ''}) {
        const paginate = payload.paginate,
            query = payload.query

        commit('GET_COMPLAINTS_REQUEST')

        return axios
            .get(`/complaints/complaints${paginate}${query}`)
            .then(response => {
                commit('GET_COMPLAINTS_SUCCESS', response.data)

                return Promise.resolve(response)
            })
            .catch(error => {
                commit('GET_COMPLAINTS_FAILURE')
                handleApiError(error, 'Pobranie listy reklamacji nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getComplaint({ commit }, complaintId) {
        return axios
            .get(`/complaints/complaints/${complaintId}`)
            .then(response => {
                commit('GET_COMPLAINT_SUCCESS', response.data)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, `Pobranie danych do reklamacji ${complaintId} nie powiodło się.`)

                return Promise.reject(error)
            })
    },
    createComplaint({ commit }, complaintData) {
        return axios
            .post('/complaints/complaints', complaintData)
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Reklamacja została dodana.'
                }, { root: true })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Dodanie reklamacji nie powiodło się.')

                return Promise.reject(error)
            })
    },
    generateReport({ commit }, {
        query = '',
        userEmail
    } = {}) {
        return axios
            .get(`/complaints/complaints?report=true${query}`, {
                timeout: 120000, // 2 min
            })
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: `Raport został wysłany na adres ${userEmail}.`
                }, { root: true })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Wygenerowanie raportu nie powiodło się.')

                return Promise.reject(error)
            })
    },
    createAttachment({ commit, state }, { formData, fileName}) {
        if (formData !== null) {
            const cancelToken = axios.CancelToken
            const cancelTokenSource = cancelToken.source()

            commit('SET_CANCEL_TOKEN', {
                token: cancelTokenSource,
                fileName: fileName
            })
        } else {
            commit('ABORT_FILE_UPLOAD', fileName)
        }

        const tokenIndex = state.uploadCancelTokens.findIndex(item => item.fileName === fileName)

        commit('FILE_PROGRESS', {
            show: true,
            value: 0,
            fileName: fileName
        }, { root: true })

        return axios
            .post('/complaints/complaints/file/upload', formData, {
                cancelToken: state.uploadCancelTokens[tokenIndex].token.token,
                timeout: 900000, // 15 min
                onUploadProgress(progressEvent) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)

                    commit('FILE_PROGRESS', {
                        show: true,
                        value: percentCompleted,
                        fileName: fileName
                    }, { root: true })
                }
            })
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                if (error.message !== 'canceled') {
                    handleApiError(error, 'Dodanie załącznika nie powiodło się.')
                }

                return Promise.reject(error)
            })
            .finally(() => {
                commit('REMOVE_CANCEL_TOKEN', tokenIndex)
                commit('FILE_PROGRESS', {
                    show: false,
                    value: 0,
                    fileName: null
                }, { root: true })
            })
    },
    deleteAttachment(context, id) {
        return axios
            .delete(`/complaints/complaints/file/upload/${id}`)
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Usunięcie załącznika nie powiodło się.')

                return Promise.reject(error)
            })
    },
    updateComplaint({ commit }, complaintData) {
        return axios
            .put(`/complaints/complaints/${complaintData.id}`, complaintData)
            .then(response => {
                // TODO tutaj jeżli robimy update przed usunięciem (sprawdzimy to przez fakt, że w odpowiedzi mamy powód usunięcia na raklamacji)
                // to nie odpalamy tej notification ponieżej
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Reklamacja została zedytowana.'
                }, { root: true })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Zapis reklamacji nie powiódł się.')

                return Promise.reject(error)
            })
    },
    getComplaintTypes({ commit }) {
        return axios
            .get('/complaints/complaints/dicts/complaint-types')
            .then(response => {
                commit('GET_COMPLAINT_TYPES_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie typów reklamacji nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getComplaintStatuses({ commit }) {
        return axios
            .get('/complaints/complaints/dicts/statuses')
            .then(response => {
                commit('GET_COMPLAINT_STATUSES_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie statusów nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getProductionDepartments({ commit }) {
        return axios
            .get('/production-departments/dicts/production-departments?showInExternalComplaint=true')
            .then(response => {
                const departments = [...(response.data.results || [])].sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '', 'pl')
                )

                commit('GET_PRODUCTION_DEPARTMENTS_SUCCESS', departments)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie działów nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getProductionSiteGroups({ commit }, productionDepartment) {
        return axios
            .get(`/production-sites/production-site-groups?productionDepartment_id=${productionDepartment}`)
            .then(response => {
                commit('GET_PRODUCTION_SITE_GROUPS_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie grup stanowisk nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getProductionProcesses({ commit }, query = {}) {
        const params = query.productionSiteGroupId
            ? `productionSiteGroupId=${query.productionSiteGroupId}`
            : `departmentId=${query.productionDepartmentId}`

        return axios
            .get(`/production-processes/processes-raw?${params}`)
            .then(response => {
                commit('GET_PRODUCTION_PROCESSES_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie procesów produkcyjnych nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getMachines({ commit }, productionSiteGroupId ) {
        return axios
            .get(`/machines/machines-raw?productionSiteGroupId=${productionSiteGroupId}`)
            .then(response => {
                commit('GET_MACHINES_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie maszyn nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getReportItems({ commit }, searchNumber ) {
        return axios
            .get(`/reports/report-items?primaryNumber=${searchNumber}&typeId=10,30&limit=15`)
            .then(response => {
                commit('GET_REPORT_ITEMS_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Wyszukiwanie zleceń nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getUsers({ commit }, search = '') {
        return axios
            .get(`/users/users?search=${search}`)
            .then(response => {
                commit('GET_USERS_SUCCESS', response.data.results)
    
                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie listy użytkowników nie powiodło się.')
    
                return Promise.reject(error)
            })
    },
    getComplaintRemoveReasons({ commit }) {
        return axios
            .get('/complaints/complaints/dicts/remove-reasons')
            .then(response => {
                commit('GET_COMPLAINT_REMOVE_REASONS_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie powodów usunięcia reklamacji nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getComplaintMaterials({ commit }) {
        return axios
            .get('/complaints/complaints/dicts/material-types')
            .then(response => {
                commit('GET_COMPLAINT_MATERIALS_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie materiałów nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getComplaintUnits({commit}) {
        return axios
            .get('/complaints/complaints/dicts/unit-types')
            .then(response => {
                commit('GET_COMPLAINT_UNITS_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie jednostek nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getErrors({commit}) {
        return axios
            .get('/complaints/complaints/complaint-errors')
            .then(response => {
                commit('GET_COMPLAINT_ERROR_SUCCESS', response.data)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie błędów nie powiodło się.')

                return Promise.reject(error)
            })
    },
    createError({ commit }, complaintErrorData) {
        return axios
            .post('/complaints/complaints/complaint-errors', complaintErrorData)
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Błąd został dodany.'
                }, {root: true})

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Dodanie błędu nie powiodło się.')

                return Promise.reject(error)
            })
    },
    getComplaintStatusHistory({ commit }, complaintId) {
        commit('SET_COMPLAINTS_LOADING', true)

        return axios
            .get(`/complaints/complaints/${complaintId}/status-history`)
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie historii statusów nie powiodło się.')

                return Promise.reject(error)
            })
            .finally(() => {
                commit('SET_COMPLAINTS_LOADING', false)
            })
    },
    deleteComplaint({ commit }, complaintId) {
        return axios
            .delete(`/complaints/complaints/${complaintId}`)
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Reklamacja została usunięta.'
                }, { root: true })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Usunięcie reklamacji nie powiodło się.')

                return Promise.reject(error)
            })
    },
    createComplaintComment({ commit }, payload) {
        return axios
            .post('/complaints/complaint-comments', payload)
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Dodano komentarz.'
                }, { root: true })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Dodanie komentarza nie powiodło się.')

                return Promise.reject(error)
            })
    },
    setComplaintsFilters({ commit }, filters) {
        commit('SET_COMPLAINTS_FILTERS', filters)
    },
    findOrder({commit}, {query, selectedOrdersProductionNumbers}) {
        return axios
            .get(`/old-production-apps/orders-to-print?search=${query}`)
            .then(response => {
                commit('UPDATE_PRODUCTION_NUMBERS_LIST', response.data.results, selectedOrdersProductionNumbers)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Szukanie zamówień nie powiodło się.')

                return Promise.reject(error)
            })
    },
    downloadComplaintCard(context, payload) {
        return axios
            .post('/complaints/print-complaint-card', payload, {
                responseType: 'arraybuffer'
            })
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Wygenerowanie karty reklamacji nie powiodło się.')

                return Promise.reject(error)
            })
    },
    setSelectedOrderNumber({ commit }, selectedOrdersProductionNumbers) {
        commit('SET_ORDER_NUMBER', selectedOrdersProductionNumbers)
    },
    setProductionNumbersList({ commit }, selectedOrdersProductionNumbers) {
        commit('UPDATE_PRODUCTION_NUMBERS', selectedOrdersProductionNumbers)
    },
    setUsers({ commit }, responsiblePerson) {
        commit('SET_USERS_LIST', responsiblePerson)
    },
    clearComplaintsFilters({ commit }) {
        commit('CLEAR_COMPLAINTS_FILTERS')
    },
}